import { z } from 'zod';

// A navigation target a tenant can configure for a tile/slide/action. Allowed
// forms: an in-app path (must start with "/") or a full http(s) URL. This
// blocks dangerous schemes (javascript:, data:, vbscript:) from being persisted
// and later rendered into an <a href> / <Link href> in the customer WebView —
// the editor lets a tenant admin type a free-text URL, so the value is
// untrusted. Empty strings are rejected so a tile can't save a dead link.
const safeHrefSchema = z
  .string()
  .refine((v) => v.startsWith('/') || /^https?:\/\//i.test(v), {
    message: 'Enter an internal path (e.g. /book) or a full https:// URL',
  });

// ─── Hero Section ────────────────────────────────────────────────
// Magazine-style composition. All editorial fields are optional — a slide
// with just `imageKey` + `href` renders as a clickable image, identical to
// the legacy behavior. Tenants who fill in eyebrow/headline/emphasis/CTA
// get the full Frame-Studio-style luxury layout.
const heroSlideSchema = z.object({
  imageKey: z.string(), // filename in home_assets bucket, e.g. "hero-0.jpg"
  href: safeHrefSchema,
  // Optional per-location scoping. When set, the slide only renders while the
  // active venue (resolved from the location-switcher cookie) matches this
  // court_locations UUID. Absent/undefined = global — shows for every venue.
  // Backward-compatible: all existing untagged slides remain global.
  locationId: z.string().uuid().optional(),
  // Legacy — kept for backward compatibility. New tenants should use
  // `headline` / `emphasis` instead. If `label` is set and `headline` is
  // empty, the renderer falls back to `label`.
  label: z.string().optional(),
  subtitle: z.string().optional(),
  // Magazine eyebrow row (top of hero)
  eyebrow: z.string().max(40).optional(),
  edition: z.string().max(12).optional(),
  // Bottom-left composition
  kicker: z.string().max(40).optional(),
  headline: z.string().max(60).optional(),
  emphasis: z.string().max(40).optional(),
  // CTA pill
  ctaLabel: z.string().max(24).optional(),
  // Magazine credit caption (very low-opacity, bottom-left)
  caption: z.string().max(40).optional(),

  // ─── Optional Arabic translations ──────────────────────────────
  // When the active locale is `ar` and the matching `_ar` field is
  // non-empty, the renderer prefers it over the default. Otherwise it
  // falls back to the default field, so leaving them blank is fine.
  label_ar: z.string().optional(),
  subtitle_ar: z.string().optional(),
  eyebrow_ar: z.string().max(40).optional(),
  edition_ar: z.string().max(12).optional(),
  kicker_ar: z.string().max(40).optional(),
  headline_ar: z.string().max(60).optional(),
  emphasis_ar: z.string().max(40).optional(),
  ctaLabel_ar: z.string().max(24).optional(),
  caption_ar: z.string().max(40).optional(),
});

const heroSectionSchema = z.object({
  type: z.literal('hero'),
  mode: z.enum(['carousel', 'static']),
  slides: z.array(heroSlideSchema).min(1).max(8),
  /** Hero height as fraction of viewport. 0.45 = today's height; 0.56 = Frame Studio. */
  heightVh: z.number().min(0.3).max(0.85).optional(),
});

// ─── Location Section ────────────────────────────────────────────
const locationSectionSchema = z.object({
  type: z.literal('location'),
});

// ─── Quick Actions ───────────────────────────────────────────────
// Capability keys a quick-action can be gated on. Kept as a literal union
// so configs can be authored before we wire each gate, and renderers can
// silently skip unknown values.
export const QUICK_ACTION_CAPABILITY_GATES = [
  'guest_passes',
  'member_qr_checkin',
  'entitlements',
  'tour_booking',
] as const;

export const QUICK_ACTION_BEHAVIOURS = ['check-in'] as const;

const quickActionSchema = z.object({
  label: z.string(),
  icon: z.string(), // lucide icon name
  href: safeHrefSchema,
  color: z.string(),
  description: z.string().optional(),
  // Optional per-location scoping — mirrors heroSlideSchema.locationId. When set,
  // the action only renders while the active venue (resolved from the
  // location-switcher cookie) matches this court_locations UUID.
  // Absent/undefined = global (shows for every venue). Existing untagged actions
  // stay global, so this is backward-compatible.
  locationId: z.string().uuid().optional(),
  // Optional image (filename in home_assets bucket, e.g. "quick-action-0.jpg").
  // When set, the action renders as an image-backed card instead of the
  // minimal icon tile.
  imageKey: z.string().optional(),
  // When set, the tile only renders if the listed capability is enabled
  // for the tenant. Lets tenants configure capability-aware tiles (e.g.
  // "Invite a Guest") in their native_home_config without the renderer
  // hard-coding any tile.
  requiresCapability: z.enum(QUICK_ACTION_CAPABILITY_GATES).optional(),
  // In-app behaviour instead of navigation. 'check-in' opens the member QR
  // sheet; `href` stays set as the fallback destination for older clients.
  action: z.enum(QUICK_ACTION_BEHAVIOURS).optional(),
  // Optional Arabic translations — fall back to defaults if blank.
  label_ar: z.string().optional(),
  description_ar: z.string().optional(),
});

// 'grid' = 2-column tiles (default). 'bar' = one card with the actions side by
// side; directly after a hero it floats over the hero's bottom edge.
export const QUICK_ACTIONS_LAYOUTS = ['grid', 'bar'] as const;

const quickActionsSectionSchema = z.object({
  type: z.literal('quick-actions'),
  actions: z.array(quickActionSchema).min(1).max(6),
  layout: z.enum(QUICK_ACTIONS_LAYOUTS).optional(),
});

// ─── Booking Category ────────────────────────────────────────────
const bookingCategorySectionSchema = z.object({
  type: z.literal('booking-category'),
  sportSlug: z.string(),
  categorySlug: z.string(), // e.g. "tournament", "event", "group_classes"
  label: z.string().optional(),
  label_ar: z.string().optional(),
});

// ─── My Bookings ─────────────────────────────────────────────────
const myBookingsSectionSchema = z.object({
  type: z.literal('my-bookings'),
});

// ─── Today Classes ───────────────────────────────────────────────
const todayClassesSectionSchema = z.object({
  type: z.literal('today-classes'),
});

// ─── Coaches ─────────────────────────────────────────────────────
const coachesSectionSchema = z.object({
  type: z.literal('coaches'),
});

export const sportsSectionSchema = z.object({
  type: z.literal('sports'),
  title: z.string().max(60).optional(),
  title_ar: z.string().max(60).optional(),
  sports: z.record(z.string().uuid(), z.object({
    icon: z.string().optional(),
    imageKey: z.string().optional(),
  })).default({}),
});

// ─── Class Categories ────────────────────────────────────────────
// Curated tiles showcasing specific class types on the native home. Stores ONLY
// the curated references + a label override; content comes from the class_type row.
const classCategoryItemSchema = z.object({
  classTypeId: z.string().uuid(),
  // Optional short display label. Blank → falls back to the class type's name
  // (name_alt in Arabic).
  label: z.string().max(40).optional(),
  label_ar: z.string().max(40).optional(),
});

const classCategoriesSectionSchema = z.object({
  type: z.literal('class-categories'),
  eyebrow: z.string().max(40).optional(),
  title: z.string().max(60).optional(),
  eyebrow_ar: z.string().max(40).optional(),
  title_ar: z.string().max(60).optional(),
  // No .min(1): a freshly-added section starts empty (renders nothing) until
  // the tenant curates tiles in the Home Screen Builder.
  // The refine lives on the array field (not the object) so this section stays
  // a plain ZodObject and remains usable in the discriminated union below.
  items: z
    .array(classCategoryItemSchema)
    .max(12)
    // A class type may appear at most once: the customer-facing render keys
    // tiles by classTypeId, so a duplicate produces repeated tiles and
    // duplicate React keys. Flag the item that collides.
    .superRefine((items, ctx) => {
      const seen = new Set<string>();
      items.forEach((item, i) => {
        if (seen.has(item.classTypeId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'This class type is already used in another tile.',
            path: [i, 'classTypeId'],
          });
        } else {
          seen.add(item.classTypeId);
        }
      });
    }),
});

// ─── Member ID card (Phase 1 member-club) ────────────────────────
// Renders the active membership's rotating QR + member number when the
// MEMBER_QR_CHECKIN capability is enabled. No payload — visibility is
// gated on capability + membership at render time.
const memberIdCardSectionSchema = z.object({
  type: z.literal('member-id-card'),
});

// ─── Guest allowance (Phase 1 member-club) ───────────────────────
// "X of Y guests left today" + invite CTA. Hidden when GUEST_PASSES
// capability is off, the user has no active membership, or the user's
// tier has an empty guest_policy.
const guestAllowanceSectionSchema = z.object({
  type: z.literal('guest-allowance'),
});

// ─── My memberships ──────────────────────────────────────────────
// Stacked list of the signed-in user's memberships at this tenant,
// rendering NativeMembershipCard rows. Hidden when the user is signed
// out or has no non-cancelled membership.
// 'cards' (default) = full membership cards; 'compact' = one slim row per
// membership linking to its detail page.
export const MY_MEMBERSHIPS_VARIANTS = ['cards', 'compact'] as const;

const myMembershipsSectionSchema = z.object({
  type: z.literal('my-memberships'),
  variant: z.enum(MY_MEMBERSHIPS_VARIANTS).optional(),
});

// ─── Activity ────────────────────────────────────────────────────
// "Recent activity" card: this month's headline count plus a day-dot grid,
// linking to the /activity calendar. Unset metric = visits when the tenant
// has member QR check-in, otherwise classes attended.
export const ACTIVITY_METRICS = ['visits', 'classes'] as const;

const activitySectionSchema = z.object({
  type: z.literal('activity'),
  metric: z.enum(ACTIVITY_METRICS).optional(),
});

// ─── Recent guest passes ─────────────────────────────────────────
// Horizontal scroller of the member's recent guest invites at this
// tenant. Gated on GUEST_PASSES capability + signed-in + ≥1 invite.
const recentGuestPassesSectionSchema = z.object({
  type: z.literal('recent-guest-passes'),
});

// ─── Greeting ────────────────────────────────────────────────────
// Editorial greeting block: time-of-day eyebrow + serif welcome with
// the user's first name + optional credits/tier line. Emotional anchor
// at the top of the home screen.
const greetingSectionSchema = z.object({
  type: z.literal('greeting'),
  /** Show "You have X credits remaining" line. */
  showCredits: z.boolean().optional(),
  /** Show membership tier label. */
  showTier: z.boolean().optional(),
});

// ─── Stats strip ─────────────────────────────────────────────────
// 2-4 stats in a hairline-bordered grid. Display serif numbers, mono labels.
// Stats keys are bound to known data sources; component fetches in one query.
export const STAT_KEYS = [
  'classes_attended',
  'credits_remaining',
  'streak_days',
  'bookings_made',
  'hours_played',
  'membership_tier',
] as const;

const statsStripSectionSchema = z.object({
  type: z.literal('stats-strip'),
  stats: z.array(z.enum(STAT_KEYS)).min(2).max(4),
});

// ─── Numbered list ───────────────────────────────────────────────
// Editorial alternative to quick-actions. The "01 / 02 / 03" pattern
// from Frame Studio's empty state — far more luxurious than colored squares.
const numberedListItemSchema = z.object({
  title: z.string().max(60),
  description: z.string().max(120).optional(),
  href: z.string(),
  // Optional Arabic translations
  title_ar: z.string().max(60).optional(),
  description_ar: z.string().max(120).optional(),
});

const numberedListSectionSchema = z.object({
  type: z.literal('numbered-list'),
  eyebrow: z.string().max(40).optional(),
  title: z.string().max(60).optional(),
  eyebrow_ar: z.string().max(40).optional(),
  title_ar: z.string().max(60).optional(),
  items: z.array(numberedListItemSchema).min(1).max(6),
  /**
   * When true, the section only renders for users with no upcoming bookings.
   * Useful as an onboarding/empty-state nudge that hides itself once the
   * user is actively booking.
   */
  onlyIfNoBookings: z.boolean().optional(),
});

// ─── Quote ───────────────────────────────────────────────────────
// Centered italic display serif + ornament + mono attribution.
// Emotional anchor at section breaks.
const quoteSectionSchema = z.object({
  type: z.literal('quote'),
  text: z.string().max(200),
  attribution: z.string().max(40).optional(),
  text_ar: z.string().max(200).optional(),
  attribution_ar: z.string().max(40).optional(),
});

// ─── Union + Config ──────────────────────────────────────────────
export const sectionConfigSchema = z.discriminatedUnion('type', [
  heroSectionSchema,
  locationSectionSchema,
  quickActionsSectionSchema,
  bookingCategorySectionSchema,
  myBookingsSectionSchema,
  todayClassesSectionSchema,
  coachesSectionSchema,
  sportsSectionSchema,
  classCategoriesSectionSchema,
  greetingSectionSchema,
  statsStripSectionSchema,
  numberedListSectionSchema,
  quoteSectionSchema,
  memberIdCardSectionSchema,
  guestAllowanceSectionSchema,
  myMembershipsSectionSchema,
  recentGuestPassesSectionSchema,
  activitySectionSchema,
]);

// ─── Tabs (bottom navigation) ────────────────────────────────────
// Configurable bottom tab bar. Tenants pick up to 5 tabs from the known
// id list. Not setting `tabs` falls back to DEFAULT_TABS (today's hardcoded
// Home / Book / Bookings / Packages / Profile).
export const TAB_IDS = [
  'home',
  'book',
  'bookings',
  'packages',
  'profile',
  'membership',
  'memberships',
  'coaches',
  'shop',
  'contact',
] as const;

const tabItemSchema = z.object({
  id: z.enum(TAB_IDS),
  label: z.string().max(20).optional(),
  label_ar: z.string().max(20).optional(),
  hidden: z.boolean().optional(),
});

export const tabsConfigSchema = z.object({
  items: z.array(tabItemSchema).min(2).max(5),
  showLabels: z.boolean().optional(),
  /** 'glass' uses backdrop-blur on tab bar surface. */
  style: z.enum(['solid', 'glass']).optional(),
});

export type TabId = (typeof TAB_IDS)[number];
export type TabsConfig = z.infer<typeof tabsConfigSchema>;

// ─── Booking page (native) ───────────────────────────────────────
// Non-section settings that shape the native /book screen. Sits alongside
// `tabs` as a global block: it configures an existing screen rather than
// adding one to the home feed.
//
// packagesDisplay controls the package upsell strip above the booking form:
//   'hidden' (default) — the strip is not rendered at all.
//   'collapsed'        — a "View available packages" disclosure the
//                        customer taps to open.
//   'expanded'         — the package list renders inline, always open,
//                        with no disclosure row.
// The strip is independently hidden when the tenant has no packages or the
// flow is locked to a single service, regardless of this setting.
export const PACKAGES_DISPLAY_MODES = [
  'collapsed',
  'expanded',
  'hidden',
] as const;

export const bookingConfigSchema = z.object({
  packagesDisplay: z.enum(PACKAGES_DISPLAY_MODES).optional(),
});

export type PackagesDisplayMode = (typeof PACKAGES_DISPLAY_MODES)[number];
export type BookingConfig = z.infer<typeof bookingConfigSchema>;

export const nativeHomeConfigSchema = z.object({
  version: z.literal(1),
  sections: z.array(sectionConfigSchema).max(12),
  tabs: tabsConfigSchema.optional(),
  booking: bookingConfigSchema.optional(),
});

// ─── Types ───────────────────────────────────────────────────────
export type NativeHomeConfig = z.infer<typeof nativeHomeConfigSchema>;
export type SectionConfig = z.infer<typeof sectionConfigSchema>;
export type SportsSectionConfig = z.infer<typeof sportsSectionSchema>;
export type HeroSlide = z.infer<typeof heroSlideSchema>;
export type HeroSectionConfig = z.infer<typeof heroSectionSchema>;
export type QuickAction = z.infer<typeof quickActionSchema>;
export type QuickActionsSectionConfig = z.infer<
  typeof quickActionsSectionSchema
>;
export type MyMembershipsSectionConfig = z.infer<
  typeof myMembershipsSectionSchema
>;
export type ActivitySectionConfig = z.infer<typeof activitySectionSchema>;
export type ActivityMetric = (typeof ACTIVITY_METRICS)[number];
export type BookingCategorySectionConfig = z.infer<
  typeof bookingCategorySectionSchema
>;
export type ClassCategoriesSectionConfig = z.infer<
  typeof classCategoriesSectionSchema
>;
export type ClassCategoryItem = z.infer<typeof classCategoryItemSchema>;
export type GreetingSectionConfig = z.infer<typeof greetingSectionSchema>;
export type StatsStripSectionConfig = z.infer<typeof statsStripSectionSchema>;
export type NumberedListSectionConfig = z.infer<
  typeof numberedListSectionSchema
>;
export type QuoteSectionConfig = z.infer<typeof quoteSectionSchema>;
export type StatKey = (typeof STAT_KEYS)[number];

// ─── Default Config ──────────────────────────────────────────────
export const DEFAULT_NATIVE_HOME_CONFIG: NativeHomeConfig = {
  version: 1,
  sections: [
    {
      type: 'quick-actions',
      actions: [
        {
          label: 'Book Now',
          icon: 'calendar-plus',
          href: '/book',
          color: '#3b82f6',
        },
        {
          label: 'My Bookings',
          icon: 'calendar',
          href: '/bookings',
          color: '#10b981',
        },
      ],
    },
    { type: 'my-bookings' },
    { type: 'today-classes' },
  ],
};

/**
 * Safely parse a native home config from JSONB, falling back to defaults.
 */
export function parseNativeHomeConfig(raw: unknown): NativeHomeConfig {
  const result = nativeHomeConfigSchema.safeParse(raw);
  return result.success ? result.data : DEFAULT_NATIVE_HOME_CONFIG;
}

/**
 * Resolves the native booking page's package-strip mode from a raw
 * native_home_config JSONB value. Falls back to 'hidden' (the default) for
 * unconfigured tenants and unparseable configs, so this is safe to call with
 * an untrusted value straight from the database.
 */
export function resolvePackagesDisplayMode(raw: unknown): PackagesDisplayMode {
  const result = nativeHomeConfigSchema.safeParse(raw);
  return result.success
    ? (result.data.booking?.packagesDisplay ?? 'hidden')
    : 'hidden';
}
